import { Placement } from '@floating-ui/react';
import { RecurrenceFormUnit } from '@griply/types';
import {
  EVERY_DAY_RRULE,
  isValidRrule,
  parseFormUnitFromRrule,
  ProviderRecurrenceLimits,
} from '@griply/utils';
import React, { useMemo, useState } from 'react';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { WeekDays } from 'shared/types/week-days';

import { Footer } from './footer';
import * as Styled from './form.style';
import { Header } from './header';
import { RruleMonthsBody } from './rrule-months-body';
import { RruleWeeksBody } from './rrule-weeks-body';
import { RruleYearsBody } from './rrule-years-body';

export type EventRepeatFormProps = {
  initialRrule?: string | null;
  weekStartsOn: WeekDays;
  onChange: (rrule: string) => void;
  onClose: () => void;
  referenceElement: React.RefObject<HTMLElement>;
  position?: Placement;
  // narrowed for a target calendar whose provider cannot store every rule Griply can express
  limits?: ProviderRecurrenceLimits;
};

// Bare-RRULE variant of RepeatForm for calendar events — no HabitSchedule,
// no target frequencies, no task-type coupling.
export const EventRepeatForm: React.FC<EventRepeatFormProps> = ({
  initialRrule,
  weekStartsOn,
  onChange,
  onClose,
  referenceElement,
  position,
  limits,
}) => {
  const [rrule, setRrule] = useState(initialRrule ?? EVERY_DAY_RRULE);

  const onSubmit = () => {
    onChange(rrule);
    onClose();
  };

  const selectedUnit = useMemo(() => parseFormUnitFromRrule(rrule), [rrule]);
  const isValidValue = useMemo(() => isValidRrule(rrule), [rrule]);

  return (
    <PopupMenu
      referenceElement={referenceElement}
      onOutside={onClose}
      position={position}
    >
      <Styled.Container>
        <Header
          onChangeUnit={setRrule}
          onChangeInterval={setRrule}
          unit={selectedUnit}
          rrule={rrule}
        />

        <Styled.Body>
          {selectedUnit === RecurrenceFormUnit.Weeks && (
            <RruleWeeksBody
              rrule={rrule}
              onChange={setRrule}
              weekStartsOn={weekStartsOn}
            />
          )}
          {selectedUnit === RecurrenceFormUnit.Months && (
            <RruleMonthsBody
              rrule={rrule}
              onChange={setRrule}
              weekStartsOn={weekStartsOn}
              limits={limits}
            />
          )}
          {selectedUnit === RecurrenceFormUnit.Years && (
            <RruleYearsBody
              rrule={rrule}
              onChange={setRrule}
              weekStartsOn={weekStartsOn}
              limits={limits}
            />
          )}
        </Styled.Body>

        <Footer onCancel={onClose} onSubmit={onSubmit} isValid={isValidValue} />
      </Styled.Container>
    </PopupMenu>
  );
};
